import { Box, Button, Card, CardContent, Grid, Hidden, IconButton, Tooltip } from '@material-ui/core';
import dayjs from 'dayjs';
import * as React from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';

import { PredefinedFiltersEnum, SnackBarVariants } from '@app/app.enum';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import Table from '@app/components/shared/table.component';
import * as constants from '@app/constants';
import * as models from '@app/models';
import { initialReportAccountStatementByDayComponentState } from '@app/models/report.models';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { ReportService } from '@app/services/report.service';
import { SharedService } from '@app/services/shared.service';
import Axios from 'axios';
import { Column, UseTableCellProps } from 'react-table';

const intlService: IntlService = Injector.get(IntlService);
const reportService: ReportService = Injector.get(ReportService);
const sharedService: SharedService = Injector.get(SharedService);
const pageSize = 20;
type IReportAccountStatementByDayComponentProps = models.IAxiosCancellation & models.ISnackBarFeature;

class ReportAccountStatementByDayComponent extends React.Component<
	IReportAccountStatementByDayComponentProps,
	models.IReportAccountStatementByDayComponentState
> {
	constructor(props: IReportAccountStatementByDayComponentProps) {
		super(props);
		this.state = initialReportAccountStatementByDayComponentState();
	}

	public async componentDidMount() {
		await this.loadData(this.state.currentPage, this.state.startDate, this.state.endDate);
	}

	public componentDidUpdate(
		prevProps: IReportAccountStatementByDayComponentProps,
		prevState: models.IReportAccountStatementByDayComponentState
	) {
		if (this.shouldFetchData(prevState)) {
			this.loadData(this.state.currentPage, this.state.startDate, this.state.endDate);
		}
	}

	public render() {
		const filterRow = this.getFilterRow();
		return (
			<React.Fragment>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Card>
							<CardContent>
								{filterRow}
								<Hidden only={['md', 'sm', 'xs']}>
									<Grid container={true}>
										<Table<models.IAccountStatementByDay>
											columns={this.getGridColumns()}
											data={this.state.results}
											dataLoading={this.state.dataLoading}
											onPageChange={(e: object, page: number) => this.setState({ currentPage: page })}
											currentPage={this.state.currentPage}
											pageSize={pageSize}
											totalRecords={this.state.totalResults}
											totalPages={this.state.totalPages}
											pageJump={this.state.pageJump}
											getCurrentPage={() => this.state.currentPage}
											getPageJump={() => this.state.pageJump}
											setCurrentPage={(page: number) => {
												this.setState({
													currentPage: page,
												});
											}}
											setPageJump={(page: number) => {
												this.setState({
													pageJump: page,
												});
											}}
										/>
									</Grid>
								</Hidden>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</React.Fragment>
		);
	}

	private getFilterRow() {
		return (
			<Grid container={true} spacing={2}>
				<Grid item={true} lg={8} md={12} sm={12} xs={12}>
					<Grid container={true} justifyContent='flex-start' spacing={2}>
						<Grid item={true} lg={4} md={4} sm={12} xs={12}>
							<PredefinedFilters
								startDate={this.state.startDate}
								endDate={this.state.endDate}
								onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
							/>
						</Grid>
						<Grid item={true} lg={4} md={4} sm={12} xs={12}>
							<CustomDatePicker
								label={intlService.GetFormattedMessage('app.balance-history.start-date-label')}
								maxDate={this.state.endDate}
								disabled={this.state.disableDatePickerFilter}
								value={this.state.startDate}
								onChange={(date: dayjs.Dayjs) => {
									const composedFunc = this.callAfterCancellingActiveRequests(() =>
										this.setState({
											results: [],
											startDate: date.toDate(),
											currentPage: 1,
										})
									);
									composedFunc();
								}}
							/>
						</Grid>
						<Grid item={true} lg={4} md={4} sm={12} xs={12}>
							<CustomDatePicker
								label={intlService.GetFormattedMessage('app.balance-history.end-date-label')}
								minDate={this.state.startDate}
								disabled={this.state.disableDatePickerFilter}
								value={this.state.endDate}
								onChange={(date: dayjs.Dayjs) => {
									const composedFunc = this.callAfterCancellingActiveRequests(() =>
										this.setState({
											results: [],
											endDate: date.toDate(),
											currentPage: 1,
										})
									);
									composedFunc();
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item={true} lg={4} md={12} sm={12} xs={12}>
					<Hidden only={['sm', 'xs', 'md']}>
						<Box height='100%' alignItems='center' display='flex'>
							<Tooltip title={intlService.GetFormattedMessage('app.common.export')}>
								<IconButton onClick={() => this.exportReport()}>
									<Icon name='tray-arrow-down' />
								</IconButton>
							</Tooltip>
						</Box>
					</Hidden>
					<Hidden only={['xl', 'lg']}>
						<Button variant='outlined' color='primary' onClick={() => this.exportReport()}>
							<Icon name='tray-arrow-down' />
							<span className='pl-1'>{intlService.GetFormattedMessage('app.common.export')}</span>
						</Button>
					</Hidden>
				</Grid>
			</Grid>
		);
	}

	private getGridColumns = (): Array<Column<models.IAccountStatementByDay>> => {
		return [
			{
				accessor: 'date',
				Header: intlService.GetFormattedMessage('app.daily-finance.date-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<FormattedDate {...constants.dateFormatOptions} value={sharedService.getNewDateIfNotExist(tableData.value)} />
				),
				disableSortBy: true,
			},
			{
				accessor: 'creditTransactions',
				Header: intlService.GetFormattedMessage('app.account-statement-by-day.credit-transactions-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<>
						<FormattedNumber value={tableData.value} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'debitTransactions',
				Header: intlService.GetFormattedMessage('app.account-statement-by-day.debit-transactions-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<>
						<FormattedNumber value={tableData.value} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'creditAmount',
				Header: intlService.GetFormattedMessage('app.account-statement-by-day.credit-amount-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'debitAmount',
				Header: intlService.GetFormattedMessage('app.account-statement-by-day.debit-amount-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'dailyBalance',
				Header: intlService.GetFormattedMessage('app.account-statement-by-day.daily-balance-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'totalBalance',
				Header: intlService.GetFormattedMessage('app.account-statement-by-day.total-balance-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountStatementByDay>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
		];
	};

	private loadData = async (requestedPage: number, startDate: Date, endDate: Date) => {
		try {
			let pageToken = sharedService.generatePaginationToken(requestedPage - 2);

			if (requestedPage === 0) {
				pageToken = undefined;
			}
			let accountStatementByDay: models.IPagedResult<models.IAccountStatementByDay> = {
				results: [],
				nextPage: '',
				previousPage: '',
				pageSize: 0,
				totalResults: 0,
				totalPages: 0,
			};
			this.setState({
				dataLoading: true,
				results: [],
			});

			accountStatementByDay = await reportService.getAccountStatementByDay(
				pageToken,
				startDate,
				endDate,
				this.props.axiosCancellationToken
			);

			this.setState({
				...accountStatementByDay,
				dataLoading: false,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	};

	/**
	 * Export report
	 */
	private exportReport = async () => {
		const res = await reportService.exportAccountStatementByDayReport(
			this.state.startDate,
			this.state.endDate,
			this.props.axiosCancellationToken
		);
		const fileName = sharedService.parseContentDisposition(
			res.request.getResponseHeader(constants.contentDispositionHeader)
		);
		sharedService.downloadFile(res.data, fileName);
	};

	private loadDataForPredefinedFilters = async (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			results: [],
			disableDatePickerFilter: filterValue !== PredefinedFiltersEnum.Custom,
			startDate,
			endDate,
		});
	};

	private shouldFetchData = (prevState: models.IReportAccountStatementByDayComponentState): boolean => {
		return (
			(prevState.startDate !== this.state.startDate ||
				prevState.endDate !== this.state.endDate ||
				prevState.currentPage !== this.state.currentPage) &&
			!this.props.isRequestCancelled
		);
	};

	private callAfterCancellingActiveRequests = (callback: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
			callback(...args);
		};
	};
}

export default withAxiosCancellation(withSnackBar(ReportAccountStatementByDayComponent));



// WEBPACK FOOTER //
// ./src/components/report/account-statement-by-day/account-statement-by-day.component.tsx